import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  increment,
  getDoc,
  serverTimestamp,
  or
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../../../services/firebase/firebase';

class ResourceService {
  constructor() {
    this.collectionRef = collection(db, 'resources');
  }

  // Fetch resources with filtering and pagination
  async fetchResources(filters = {}, lastDoc = null, pageSize = 10, userUniversity = null) {
    try {
      let q = query(this.collectionRef);

      // Apply university filtering - show resources from user's university or all universities
      if (userUniversity && filters.universityFilter !== 'all') {
        if (filters.universityFilter === 'my-university') {
          // Show only resources from user's university (or marked as all universities)
          q = query(q, or(
            where('university', '==', userUniversity),
            where('university', '==', 'All Universities'),
            where('applicableToAllUniversities', '==', true)
          ));
        } else if (filters.universityFilter === 'all-universities') {
          // Show only resources marked as applicable to all universities
          q = query(q, or(
            where('university', '==', 'All Universities'),
            where('applicableToAllUniversities', '==', true)
          ));
        }
        // If no universityFilter is set, show all resources (default behavior)
      } else if (userUniversity) {
        // Default behavior: show resources from user's university + all universities
        q = query(q, or(
          where('university', '==', userUniversity),
          where('university', '==', 'All Universities'),
          where('applicableToAllUniversities', '==', true)
        ));
      }

      // Apply other filters
      if (filters.subject && filters.subject !== 'all') {
        q = query(q, where('subject', '==', filters.subject));
      }

      if (filters.resourceType && filters.resourceType !== 'all') {
        q = query(q, where('resourceType', '==', filters.resourceType));
      }

      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      } else {
        // Default to showing only active resources
        q = query(q, where('status', '==', 'active'));
      }

      // Apply sorting
      const sortBy = filters.sortBy || 'createdAt';
      const sortOrder = filters.sortOrder || 'desc';
      q = query(q, orderBy(sortBy, sortOrder));

      // Apply pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      q = query(q, limit(pageSize));

      const snapshot = await getDocs(q);
      const resources = [];
      
      snapshot.forEach((doc) => {
        resources.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        resources,
        lastDoc: snapshot.docs[snapshot.docs.length - 1],
        hasMore: snapshot.docs.length === pageSize
      };
    } catch (error) {
      console.error('Error fetching resources:', error);
      throw error;
    }
  }

  // Get unique subjects for filtering
  async getSubjects() {
    try {
      const snapshot = await getDocs(query(this.collectionRef, where('status', '==', 'active')));
      const subjects = new Set();
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.subject) {
          subjects.add(data.subject);
        }
      });

      return Array.from(subjects).sort();
    } catch (error) {
      console.error('Error fetching subjects:', error);
      throw error;
    }
  }

  // Get unique universities for filtering
  async getUniversities() {
    try {
      const snapshot = await getDocs(query(this.collectionRef, where('status', '==', 'active')));
      const universities = new Set();
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.submittedByUniversity && data.submittedByUniversity !== 'All Universities') {
          universities.add(data.submittedByUniversity);
        }
        if (data.university && data.university !== 'All Universities') {
          universities.add(data.university);
        }
      });

      return Array.from(universities).sort();
    } catch (error) {
      console.error('Error fetching universities:', error);
      throw error;
    }
  }

  // Upload a new resource
  async uploadResource(resourceData, file = null) {
    try {
      let fileUrl = resourceData.link || resourceData.fileUrl || '';
      let thumbnailUrl = null;

      // Handle file upload if provided
      if (file) {
        const fileRef = ref(storage, `resources/${Date.now()}_${file.name}`);
        const uploadResult = await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(uploadResult.ref);
      }

      // Generate thumbnail for YouTube videos
      if (resourceData.resourceType === 'video' && fileUrl.includes('youtube.com')) {
        const videoId = this.extractYouTubeVideoId(fileUrl);
        if (videoId) {
          thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        }
      }

      const newResource = {
        title: resourceData.title,
        description: resourceData.description,
        subject: resourceData.subject,
        resourceType: resourceData.resourceType,
        link: fileUrl,
        fileUrl: fileUrl, // Keep both for backwards compatibility
        thumbnailUrl,
        submittedBy: resourceData.submittedBy,
        
        // University information
        university: resourceData.university || resourceData.submittedByUniversity,
        submittedByUniversity: resourceData.submittedByUniversity,
        applicableToAllUniversities: resourceData.applicableToAllUniversities || false,
        
        status: 'pending', // All uploads start as pending
        likes: 0,
        dislikes: 0,
        views: 0,
        createdAt: serverTimestamp(),
        userInteraction: 'none'
      };

      const docRef = await addDoc(this.collectionRef, newResource);
      return {
        id: docRef.id,
        ...newResource
      };
    } catch (error) {
      console.error('Error uploading resource:', error);
      throw error;
    }
  }

  // Update resource (like/dislike/views)
  async updateResource(resourceId, updates) {
    try {
      const resourceRef = doc(db, 'resources', resourceId);
      await updateDoc(resourceRef, updates);
      return true;
    } catch (error) {
      console.error('Error updating resource:', error);
      throw error;
    }
  }

  // Increment views
  async incrementViews(resourceId) {
    try {
      const resourceRef = doc(db, 'resources', resourceId);
      await updateDoc(resourceRef, {
        views: increment(1)
      });
      return true;
    } catch (error) {
      console.error('Error incrementing views:', error);
      throw error;
    }
  }

  // Like a resource
  async likeResource(resourceId, userId) {
    try {
      const resourceRef = doc(db, 'resources', resourceId);
      const resourceDoc = await getDoc(resourceRef);
      
      if (!resourceDoc.exists()) {
        throw new Error('Resource not found');
      }

      const data = resourceDoc.data();
      const currentInteraction = data.userInteraction || 'none';

      let updates = {};

      if (currentInteraction === 'liked') {
        // Unlike
        updates = {
          likes: increment(-1),
          userInteraction: 'none'
        };
      } else if (currentInteraction === 'disliked') {
        // Change from dislike to like
        updates = {
          likes: increment(1),
          dislikes: increment(-1),
          userInteraction: 'liked'
        };
      } else {
        // New like
        updates = {
          likes: increment(1),
          userInteraction: 'liked'
        };
      }

      await updateDoc(resourceRef, updates);
      return true;
    } catch (error) {
      console.error('Error liking resource:', error);
      throw error;
    }
  }

  // Dislike a resource
  async dislikeResource(resourceId, userId) {
    try {
      const resourceRef = doc(db, 'resources', resourceId);
      const resourceDoc = await getDoc(resourceRef);
      
      if (!resourceDoc.exists()) {
        throw new Error('Resource not found');
      }

      const data = resourceDoc.data();
      const currentInteraction = data.userInteraction || 'none';

      let updates = {};

      if (currentInteraction === 'disliked') {
        // Remove dislike
        updates = {
          dislikes: increment(-1),
          userInteraction: 'none'
        };
      } else if (currentInteraction === 'liked') {
        // Change from like to dislike
        updates = {
          likes: increment(-1),
          dislikes: increment(1),
          userInteraction: 'disliked'
        };
      } else {
        // New dislike
        updates = {
          dislikes: increment(1),
          userInteraction: 'disliked'
        };
      }

      await updateDoc(resourceRef, updates);
      return true;
    } catch (error) {
      console.error('Error disliking resource:', error);
      throw error;
    }
  }

  // Delete a resource
  async deleteResource(resourceId, userId) {
    try {
      const resourceRef = doc(db, 'resources', resourceId);
      const resourceDoc = await getDoc(resourceRef);
      
      if (!resourceDoc.exists()) {
        throw new Error('Resource not found');
      }

      const data = resourceDoc.data();
      
      // Check if user owns this resource
      if (data.submittedBy !== userId) {
        throw new Error('Unauthorized to delete this resource');
      }

      // Delete associated file from storage if it exists
      if (data.fileUrl && data.fileUrl.includes('firebasestorage.googleapis.com')) {
        try {
          const fileRef = ref(storage, data.fileUrl);
          await deleteObject(fileRef);
        } catch (fileError) {
          console.warn('Could not delete file from storage:', fileError);
        }
      }

      await deleteDoc(resourceRef);
      return true;
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  }

  // Get resource by ID
  async getResourceById(resourceId) {
    try {
      const resourceRef = doc(db, 'resources', resourceId);
      const resourceDoc = await getDoc(resourceRef);
      
      if (!resourceDoc.exists()) {
        throw new Error('Resource not found');
      }

      return {
        id: resourceDoc.id,
        ...resourceDoc.data()
      };
    } catch (error) {
      console.error('Error getting resource:', error);
      throw error;
    }
  }

  // Helper function to extract YouTube video ID
  extractYouTubeVideoId(url) {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  }

  // Get resources by user
  async getUserResources(userId) {
    try {
      const q = query(
        this.collectionRef,
        where('submittedBy', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const resources = [];
      
      snapshot.forEach((doc) => {
        resources.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return resources;
    } catch (error) {
      console.error('Error fetching user resources:', error);
      throw error;
    }
  }
}

export default new ResourceService();